import type { ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useStore } from '../stores/store';
import { cacheRead } from '../api/client';
import { ErrorBoundary } from './ErrorBoundary';

const Shell = styled.div`
  min-height: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  padding-bottom: 72px; /* deja lugar a la bottom nav */
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.accentText};
`;

const Brand = styled.div`
  font-weight: 800;
  letter-spacing: 0.5px;
  font-size: 1.1rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconBtn = styled.button`
  background: rgba(255, 255, 255, 0.18);
  color: inherit;
  border: none;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 1rem;
`;

const Main = styled.main`
  padding: 12px 16px;
`;

const Nav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  a {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    font-size: 0.78rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
  a.active {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 700;
  }
  a span {
    display: block;
    font-size: 2.5rem;
    line-height: 1.2;
  }
`;

const HOY = { to: '/', icon: '⚽', label: 'Hoy' };
const BASE_TABS = [
  { to: '/fixtures', icon: '📅', label: 'Partidos' },
  { to: '/groups', icon: '🏆', label: 'Zonas' },
  { to: '/bracket', icon: '🗝️', label: 'Llave' },
  { to: '/resumen', icon: '🏅', label: 'Resumen' },
  { to: '/historico', icon: '📚', label: 'Histórico' },
  { to: '/venues', icon: '🏟️', label: 'Sedes' },
];

const USO = { to: '/uso', icon: '📈', label: 'Uso' };

export function Layout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useStore();
  const adminKey = useStore((s) => s.adminKey);

  // Mientras dura el Mundial: "Hoy" primero. Al terminar (remaining 0): se oculta "Hoy"
  // y "Resumen" pasa a ser principal. "Uso" solo aparece si se desbloqueó el acceso.
  const t = cacheRead<{ remaining: number }>('/tournament');
  const finished = t != null && t.remaining === 0;
  const TABS = [
    ...(finished ? [] : [HOY]),
    ...BASE_TABS,
    ...(adminKey ? [USO] : []),
  ];

  return (
    <Shell>
      <Header>
        <Brand>VamoVamo!</Brand>
        <Actions>
          {!adminKey && (
            <IconBtn onClick={() => navigate('/acceso')} aria-label="Acceso a estadísticas" title="Acceso">
              🔑
            </IconBtn>
          )}
          <IconBtn onClick={toggleTheme} aria-label="Cambiar tema">
            {theme === 'dark' ? '☀️' : '🌙'}
          </IconBtn>
        </Actions>
      </Header>
      <Main>
        <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
      </Main>
      <Nav>
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.to === '/'}>
            <span>{t.icon}</span>
            {t.label}
          </NavLink>
        ))}
      </Nav>
    </Shell>
  );
}
